import React, {useEffect, useState} from 'react';
import {Autosuggest, Select} from '@amzn/awsui-components-react/polaris';
import {DataKeyInput} from 'src/models/common/DataSourceRecord';
import CONSTANTS from 'src/utils/constants';
import ServiceCollection from 'src/services/ServiceCollection';
import TPEAction from 'src/models/common/TPEAction';
import 'src/assets/styles/MagicBox.scss';
import Placeholder from 'src/models/tp-allocation/Placeholder';
import {Box, Button} from "@amzn/awsui-components-react";
import {MultiAllocationSheetData} from "src/models/multidimensional-allocation/MultiAllocationSheetData";

interface MagicBoxActions {
    SET_FX_INPUTS: TPEAction,
    SET_DATA_KEY_INPUT: TPEAction,
    SET_DATA_KEY_COA_INPUT: TPEAction,
    TOGGLE_CATEGORY_EXPANSION: TPEAction,
    UPDATE_RECORD_FIELD: TPEAction,
}

/**
 * COA input interface for multidimensional allocation with single-value autosuggestion.
 * Displays counters (0 or 1) and provides Company/GL Account inputs with selected COA segments.
 */
export default function MultidimensionalAllocationDataSourcesMagicBox(props: {
    recordBeingEdited: any | undefined,
    services: ServiceCollection,
    dispatch: React.Dispatch<TPEAction>,
    coaTypes: string[],
    sevenSegmentLOV: Map<string, string[]>,
    selectedCustomCoaValues?: Map<string, string>,
    setPullDataSourceBalance?: (datasourceId: string) => void,
    ACTIONS: MagicBoxActions,
    filteredAllocationSheets: MultiAllocationSheetData[],
    allocationSheetsLoading: boolean,
    pullBalanceLoading?: boolean,
    isViewingHistoricalData?: boolean
}) {
    const {
        recordBeingEdited,
        services,
        dispatch,
        coaTypes,
        selectedCustomCoaValues,
        sevenSegmentLOV,
        setPullDataSourceBalance,
        ACTIONS,
        filteredAllocationSheets,
        allocationSheetsLoading,
        pullBalanceLoading = false,
        isViewingHistoricalData = false
    } = props;
    const {
        dataKeyInput = {selectedCompanies: '', selectedAccounts: '', selectedCOA: new Map<string, string>()},
        errors = new Map<string, string>(),
        isEditing = false,
    } = recordBeingEdited || {};
    const {selectedCompanies, selectedAccounts, selectedCOA} = dataKeyInput;

    const getSingleCount = (value: string) => {
        if (value == null || value.trim() == '') {
            return 0;
        }
        return 1;
    };

    const buildDataKeyCounter = () => {
        const counterArray: any[] = [];
        coaTypes.filter((coaType) => selectedCOA.has(coaType)).map((coaType) => counterArray.push({
            key: coaType,
            count: getSingleCount(selectedCOA.get(coaType) || '')
        }));
        return counterArray;
    }

    const pullBalance = () => {
        dispatch(ACTIONS.SET_FX_INPUTS);
        if (setPullDataSourceBalance) {
            setPullDataSourceBalance(new Date().toISOString())
        }
    }

    return (
        <div className="dataKeyTable">
            <div>
                <span className="dataKeyCounterSpacer">Company: {getSingleCount(selectedCompanies)}</span>
                <span className="dataKeyCounterSpacer">GL Accounts: {getSingleCount(selectedAccounts)}</span>
                {buildDataKeyCounter().map((dataKey) => <span key={dataKey.key}
                                                              className="dataKeyCounterSpacer">{dataKey.key}: {dataKey.count}</span>)}
            </div>
            <div className="dataKeyTableMainSelection">
                <div className="dataKeyTableContainerSelection">
                    <DataKeyInputLeftColumn key="dkil" coaTypes={coaTypes} selectedCOA={selectedCOA} errors={errors}/>
                    <DataKeyInputRightColumn key="dkir"
                                             isEditing={isEditing}
                                             dataKeyInput={dataKeyInput}
                                             services={services}
                                             dispatch={dispatch}
                                             ACTIONS={ACTIONS}
                                             coaTypes={coaTypes}
                                             sevenSegmentLOV={sevenSegmentLOV}
                                             selectedCustomCoaValues={selectedCustomCoaValues}
                                             recordBeingEdited={recordBeingEdited}
                                             filteredAllocationSheets={filteredAllocationSheets}
                                             allocationSheetsLoading={allocationSheetsLoading}
                    />
                </div>
                {!isViewingHistoricalData &&
                    <div className="dataKeyTableMainSelectionButton">
                        {<Box textAlign="center"><Button
                            variant="normal"
                            disabled={isEditing || pullBalanceLoading}
                            disabledReason={pullBalanceLoading ? "Pulling balance..." : "Please save the data source before pulling balance"}
                            loading={pullBalanceLoading}
                            onClick={pullBalance}>
                            {pullBalanceLoading ? 'Pulling balance...' : 'Pull balance'}
                        </Button></Box>}
                    </div>
                }
            </div>
        </div>
    )
}

/**
 * Displays error messages for COA segments.
 * @param props
 */
function COAErrorDisplay(props: { coaSegment: string, errors: Map<string, string> }) {
    const {coaSegment, errors} = props;

    if (errors.has(coaSegment)) {
        const coaErrors = errors.get(coaSegment) || '';
        if (coaErrors.includes('Invalid value(s): ')) {
            return <span className="dataSourceInlineError">*Invalid value(s)</span>;
        } else {
            return <span className="dataSourceInlineError">*{coaErrors}</span>;
        }
    }
    return <span>&nbsp;</span>;
}

/**
 * Renders left column with COA segment labels and error displays.
 * @param props
 */
function DataKeyInputLeftColumn(props: {
    coaTypes: string[],
    selectedCOA: Map<string, string>,
    errors: Map<string, string>
}) {
    const {coaTypes, selectedCOA, errors} = props;

    return (
        <div className="magicGridStickLeft">
            <div className="magicGridLeft"><span>Company*</span><COAErrorDisplay
                coaSegment={CONSTANTS.COA_SEGMENT_MAPPING.COMPANY.UI} errors={errors}/></div>
            <div className="magicGridLeft"><span>GL Account*</span><COAErrorDisplay
                coaSegment={CONSTANTS.COA_SEGMENT_MAPPING.GL_ACCOUNT.UI} errors={errors}/></div>
            {selectedCOA != undefined && coaTypes.filter((coaType) => selectedCOA.has(coaType)).map((coaType) =>
                <div key={coaType} className="magicGridLeft"><span>{coaType}</span><COAErrorDisplay coaSegment={coaType}
                                                                                                    errors={errors}/>
                </div>)
            }
            <div className="magicGridLeftDropDown"><span>Allocation Sheet*</span><COAErrorDisplay
                coaSegment="allocationSheetId" errors={errors}/></div>
            <div className="magicGridLeft"><span>Total Balance Pull</span></div>
        </div>
    )
}

/**
 * Single-value autosuggest component for COA input.
 * @param props
 */
function COAAutosuggest(props: {
    className: string,
    lov: string[] | undefined,
    coaType: string,
    initialValue: string,
    isEditing: boolean,
    selectedCustomCoaValues?: Map<string, string>,
    placeholders?: Placeholder[],
    onBlur: (coaType: string, value: string) => any
}) {
    const {
        className,
        lov,
        coaType,
        initialValue,
        onBlur,
        isEditing,
        selectedCustomCoaValues,
        placeholders = []
    } = props;
    const [options, setOptions] = React.useState([] as any[]);
    const [value, setValue] = React.useState(initialValue);

    useEffect(() => {
        if (selectedCustomCoaValues?.has(coaType)) {
            const newValue = selectedCustomCoaValues.get(coaType) || '';
            handleChange(newValue);
        }
    }, [selectedCustomCoaValues])

    const handleLoadItems = (event: any) => {
        let {filteringText} = event.detail;

        const placeholderOptions = placeholders.map(x => {
            return {value: `<${x.placeholderName}>`}
        });

        if (filteringText == null || filteringText == '') {
            setOptions([...placeholderOptions]);
            return;
        }

        const filteredOptions = lov?.filter(x => x.toLowerCase().startsWith(filteringText.toLowerCase())).map((val) => {
            return {value: val}
        });
        setOptions(filteredOptions ? [...placeholderOptions, ...filteredOptions] : [...placeholderOptions]);
    }

    const beautifyCOAValue = () => {
        if (value == null) {
            return '';
        }
        // For single values, just trim
        const beautifiedString = value.trim();
        setValue(beautifiedString);
        return beautifiedString;
    }

    const handleChange = (selectedValue: any) => {
        setValue(selectedValue);
    }

    const handleSelect = (selectedValue: string) => {
        setValue(selectedValue);
    }

    return (
        <Autosuggest
            data-class={`coaAutosuggest ${className}`}
            onChange={({detail}) => handleChange(detail.value)}
            onBlur={() => {
                onBlur(coaType, beautifyCOAValue());
            }}
            onSelect={({detail}) => handleSelect(detail.value)}
            value={value}
            options={options}
            enteredTextLabel={val => `Use: "${val}"`}
            placeholder="Select or enter a value"
            empty="No matches found"
            loadingText="Loading"
            onLoadItems={handleLoadItems}
            filteringType="manual"
            statusType={lov == undefined ? 'loading' : 'finished'}
            disabled={!isEditing}
        />
    )
}

/**
 * Renders right column with COA input fields and allocation sheet selector.
 * @param props
 */
function DataKeyInputRightColumn(props: {
                                     isEditing: boolean,
                                     dataKeyInput: DataKeyInput,
                                     services: ServiceCollection,
                                     dispatch: React.Dispatch<TPEAction>,
                                     coaTypes: string[],
                                     sevenSegmentLOV: Map<string, string[]>,
                                     selectedCustomCoaValues?: Map<string, string>,
                                     placeholdersMap?: Map<string, Placeholder[]>,
                                     ACTIONS: MagicBoxActions,
                                     recordBeingEdited: any,
                                     filteredAllocationSheets: MultiAllocationSheetData[],
                                     allocationSheetsLoading: boolean
                                 }
) {
    const {
        isEditing,
        services,
        dispatch,
        coaTypes,
        sevenSegmentLOV,
        dataKeyInput,
        ACTIONS,
        selectedCustomCoaValues,
        placeholdersMap,
        recordBeingEdited,
        filteredAllocationSheets
    } = props;
    const {selectedCompanies, selectedAccounts, selectedCOA} = dataKeyInput;

    // This is needed for refreshing the auto suggest components for viewing the data key for different records in succession
    // The disabled auto suggest does not refresh with the updated values without changing the key
    const [keyCount, setKeyCount] = useState(0);

    useEffect(() => {
        setKeyCount(keyCount + 1);
    }, [isEditing])

    const setSelectedDataKey = (key: string, value: string) => {
        dispatch(ACTIONS.SET_DATA_KEY_INPUT.withPayload({key: key, value: value}));
    };

    const setSelectedCoa = (key: string, value: string) => {
        dispatch(ACTIONS.SET_DATA_KEY_COA_INPUT.withPayload({key: key, value: value}));
    };

    const handleSheetSelection = ({detail}: any) => {
        const selectedSheet = detail.selectedOption?.value;
        if (selectedSheet && filteredAllocationSheets) {
            const sheet = filteredAllocationSheets.find((s: MultiAllocationSheetData) => s.sheetId === selectedSheet);
            if (sheet) {
                // Update both sheetId and sheetVersion using UPDATE_RECORD_FIELD action
                dispatch(ACTIONS.UPDATE_RECORD_FIELD.withPayload({
                    fieldName: 'allocationSheetId',
                    value: sheet.sheetId
                }));
                dispatch(ACTIONS.UPDATE_RECORD_FIELD.withPayload({
                    fieldName: 'allocationSheetVersion',
                    value: sheet.sheetVersion
                }));
            }
        }
    };

    // Create sheet options from the filtered sheets
    const sheetOptions = filteredAllocationSheets?.map((sheet: MultiAllocationSheetData) => ({
        label: `${sheet.sheetName}`,
        value: sheet.sheetId
    })) || [];

    // Find current selection
    const currentSelection = recordBeingEdited?.allocationSheetId ?
        sheetOptions.find((option: any) => option.value === recordBeingEdited.allocationSheetId) || null : null;

    return (
        <div className="magicGridRightContainer">
            <div className="magicGridRightInput">
                <COAAutosuggest
                    key={`companyAutoSuggest${keyCount}`}
                    className="companiesInput"
                    coaType="selectedCompanies"
                    initialValue={selectedCompanies}
                    onBlur={setSelectedDataKey}
                    lov={sevenSegmentLOV.get(CONSTANTS.COA_SEGMENT_MAPPING.COMPANY.UI)}
                    isEditing={isEditing}
                    selectedCustomCoaValues={selectedCustomCoaValues}
                    placeholders={placeholdersMap != null ? placeholdersMap.get(CONSTANTS.COA_SEGMENT_MAPPING.COMPANY.UI) : undefined}
                />
            </div>
            <div className="magicGridRightInput">
                <COAAutosuggest
                    key={`accountAutoSuggest${keyCount}`}
                    className="accountsInput"
                    coaType="selectedAccounts"
                    initialValue={selectedAccounts}
                    onBlur={setSelectedDataKey}
                    lov={sevenSegmentLOV.get(CONSTANTS.COA_SEGMENT_MAPPING.GL_ACCOUNT.UI)}
                    isEditing={isEditing}
                    selectedCustomCoaValues={selectedCustomCoaValues}
                    placeholders={placeholdersMap != null ? placeholdersMap.get(CONSTANTS.COA_SEGMENT_MAPPING.GL_ACCOUNT.UI) : undefined}
                />
            </div>
            {selectedCOA != undefined && coaTypes.filter((coaType) => selectedCOA.has(coaType)).map((coaType) =>
                <div className="magicGridRightInput">
                    <COAAutosuggest
                        key={`${coaType.replace(' ', '_')}AutoSuggest${keyCount}`}
                        className={`${coaType.replace(' ', '_')}Input`}
                        coaType={coaType}
                        initialValue={selectedCOA.get(coaType) || ''}
                        onBlur={setSelectedCoa}
                        lov={sevenSegmentLOV.get(coaType)}
                        isEditing={isEditing}
                        selectedCustomCoaValues={selectedCustomCoaValues}
                    />
                </div>
            )}
            <div className="magicGridRightInput">
                <Select
                    key={`allocationSheetSelect${keyCount}`}
                    className="allocationSheetInput"
                    placeholder="Select allocation sheet"
                    selectedOption={currentSelection}
                    onChange={handleSheetSelection}
                    options={sheetOptions}
                    empty={"No allocation sheet available for selected segment"}
                    disabled={!isEditing}
                />
            </div>

            <div className="totalBalanceField">
                {recordBeingEdited?.totalBalance || '-'}
            </div>
        </div>
    )
}
